import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import type { InteractiveAnswer, Question, QuestionAnswerEntry, QuestionBlockPayload } from '@/_shared/types';

import { questionAnswered, questionDismissed } from './answer-helpers';
import { InteractiveCard } from './InteractiveCard';
import { MultiSelectList } from './MultiSelectList';
import { QuestionSubmitButton } from './QuestionSubmitButton';
import {
  buildMultiEntry,
  buildSingleEntry,
  multiSubmitDisabledReason,
  singleSubmitDisabledReason,
  validDefaults,
} from './selection';
import { SingleSelectList } from './SingleSelectList';

type StepState =
  | { kind: 'single'; value: string | null; otherText: string }
  | { kind: 'multi'; checked: Set<string>; otherChecked: boolean; otherText: string };

function initStepState(q: Question): StepState {
  return q.multiSelect
    ? { kind: 'multi', checked: new Set(validDefaults(q)), otherChecked: false, otherText: '' }
    : { kind: 'single', value: validDefaults(q)[0] ?? null, otherText: '' };
}

/** Empty state (ignores defaults) — a skipped step resets to this so it reads as unanswered on Back. */
function clearedStepState(q: Question): StepState {
  return q.multiSelect
    ? { kind: 'multi', checked: new Set(), otherChecked: false, otherText: '' }
    : { kind: 'single', value: null, otherText: '' };
}

function entryForStep(q: Question, s: StepState): QuestionAnswerEntry {
  if (s.kind === 'single') {
    return buildSingleEntry(q, s.value, s.otherText) ?? { questionId: q.id, kind: 'skipped' };
  }
  return buildMultiEntry(q, s.checked, s.otherChecked, s.otherText);
}

interface Props {
  payload: QuestionBlockPayload;
  onResolve: (answer: InteractiveAnswer) => void;
  submitting?: boolean;
}

/**
 * Multi-question wizard (2–5 questions, one at a time). A `k/N` badge is the only progress affordance.
 * Back preserves all entered answers; Skip records a skip sentinel; the final step submits every
 * answer at once. Dismissing at any step drops the whole block (partial answers discarded).
 */
export function QuestionWizard({ payload, onResolve, submitting }: Props): React.ReactElement {
  const questions = payload.questions;
  const [step, setStep] = useState(0);
  const [states, setStates] = useState<StepState[]>(() => questions.map(initStepState));
  const [skipped, setSkipped] = useState<boolean[]>(() => questions.map(() => false));

  const q = questions[step]!;
  const state = states[step]!;
  const isLast = step === questions.length - 1;
  // Single source for "can this step advance": null means yes, otherwise it is the tooltip reason.
  const disabledReason =
    state.kind === 'single'
      ? singleSubmitDisabledReason(state.value, state.otherText)
      : multiSubmitDisabledReason(q, state.checked, state.otherChecked, state.otherText);

  const patchState = (next: Partial<StepState>): void =>
    setStates((prev) => prev.map((s, i) => (i === step ? ({ ...s, ...next } as StepState) : s)));
  const markSkipped = (idx: number, v: boolean): boolean[] => {
    const next = skipped.map((s, i) => (i === idx ? v : s));
    setSkipped(next);
    return next;
  };

  const finish = (skipFlags: boolean[]): void => {
    const entries = questions.map(
      (qq, i): QuestionAnswerEntry =>
        skipFlags[i] ? { questionId: qq.id, kind: 'skipped' } : entryForStep(qq, states[i]!),
    );
    onResolve(questionAnswered(payload.blockId, entries));
  };

  const onNext = (): void => {
    const flags = markSkipped(step, false);
    if (isLast) finish(flags);
    else setStep(step + 1);
  };
  const onSkip = (): void => {
    const flags = markSkipped(step, true);
    // Clear this step's selection so it reads as unanswered when navigated back to (finish uses the
    // skip sentinel regardless, so dropping the stale selection here is purely the visual reset).
    setStates((prev) => prev.map((s, i) => (i === step ? clearedStepState(questions[i]!) : s)));
    if (isLast) finish(flags);
    else setStep(step + 1);
  };

  return (
    <InteractiveCard
      title={q.prompt}
      leading={
        <Badge variant="highlight" className="mt-0.5 tabular-nums">
          {step + 1}/{questions.length}
        </Badge>
      }
      stats={payload.stats}
      onDismiss={() => onResolve(questionDismissed(payload.blockId))}
      dismissDisabled={submitting}
      busy={submitting}
      ariaLabel={q.prompt}
      footerLeft={
        step > 0 ? (
          <Button variant="secondary" disabled={submitting} Icon={<ChevronLeft />} onClick={() => setStep(step - 1)}>
            Back
          </Button>
        ) : undefined
      }
      footerRight={
        <>
          <Button variant="secondary" disabled={submitting} onClick={onSkip}>
            Skip
          </Button>
          <QuestionSubmitButton disabledReason={disabledReason} loading={submitting} onClick={onNext}>
            {isLast ? 'Submit' : 'Next'}
          </QuestionSubmitButton>
        </>
      }
    >
      {/* Announce step changes to screen readers — the k/N badge + new prompt are visual-only otherwise. */}
      <span className="sr-only" aria-live="polite">
        Step {step + 1} of {questions.length}: {q.prompt}
      </span>
      {state.kind === 'single' ? (
        <SingleSelectList
          question={q}
          value={state.value}
          onValueChange={(value) => patchState({ value })}
          otherText={state.otherText}
          onOtherTextChange={(otherText) => patchState({ otherText })}
          disabled={submitting}
        />
      ) : (
        <MultiSelectList
          question={q}
          checked={state.checked}
          onCheckedChange={(checked) => patchState({ checked })}
          otherChecked={state.otherChecked}
          onOtherCheckedChange={(otherChecked) => patchState({ otherChecked })}
          otherText={state.otherText}
          onOtherTextChange={(otherText) => patchState({ otherText })}
          disabled={submitting}
        />
      )}
    </InteractiveCard>
  );
}
